/**
 * React Query hook for transport rules
 *
 * Provides data fetching and mutations for tenant Exchange transport rules
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExchangeRulesApiService } from '../services/transportRulesApi';
import type {
  ExchangeRuleResponse,
  ExchangeRuleRequest,
  ExchangeRuleUpdateRequest,
} from '../types/transportRules';

/**
 * Query key factory for transport rules
 */
export const transportRulesQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'transportRules'] as const;

/**
 * Update rule request with ruleId
 */
export interface UpdateRuleParams {
  ruleId: string;
  request: ExchangeRuleUpdateRequest;
}

/**
 * Hook return type for useTransportRules
 */
export interface UseTransportRulesResult {
  /** Transport rules data */
  rules: ExchangeRuleResponse[] | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Error from fetching rules */
  error: Error | null;
  /** Refetch rules */
  refetch: () => void;
  /** Function to create a rule */
  createRule: (request: ExchangeRuleRequest) => Promise<ExchangeRuleResponse>;
  /** Loading state for create mutation */
  isCreating: boolean;
  /** Error from create mutation */
  createError: Error | null;
  /** Function to update a rule */
  updateRule: (params: UpdateRuleParams) => Promise<void>;
  /** Loading state for update mutation */
  isUpdating: boolean;
  /** Error from update mutation */
  updateError: Error | null;
  /** Function to delete a rule */
  deleteRule: (ruleId: string) => Promise<void>;
  /** Loading state for delete mutation */
  isDeleting: boolean;
  /** Error from delete mutation */
  deleteError: Error | null;
}

/**
 * React Query hook for managing transport rules
 *
 * @param tenantId - Tenant ID to fetch transport rules for
 * @returns Transport rules data, loading states, and mutation functions
 */
export function useTransportRules(tenantId: string): UseTransportRulesResult {
  const queryClient = useQueryClient();

  // Query for fetching all transport rules
  const {
    data: rulesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: transportRulesQueryKey(tenantId),
    queryFn: async () => {
      const response = await ExchangeRulesApiService.listRules(tenantId);
      return response.rules;
    },
  });

  // Mutation for creating a rule
  const {
    mutateAsync: createRule,
    isPending: isCreating,
    error: createError,
  } = useMutation({
    mutationFn: (request: ExchangeRuleRequest) =>
      ExchangeRulesApiService.createRule(tenantId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transportRulesQueryKey(tenantId),
      });
    },
  });

  // Mutation for updating a rule
  const {
    mutateAsync: updateRule,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: ({ ruleId, request }: UpdateRuleParams) =>
      ExchangeRulesApiService.updateRule(tenantId, ruleId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transportRulesQueryKey(tenantId),
      });
    },
  });

  // Mutation for deleting a rule
  const {
    mutateAsync: deleteRule,
    isPending: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: (ruleId: string) =>
      ExchangeRulesApiService.deleteRule(tenantId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transportRulesQueryKey(tenantId),
      });
    },
  });

  return {
    rules: rulesData,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
    createRule,
    isCreating,
    createError: createError as Error | null,
    updateRule,
    isUpdating,
    updateError: updateError as Error | null,
    deleteRule,
    isDeleting,
    deleteError: deleteError as Error | null,
  };
}
